/* eslint-disable react/jsx-no-bind */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactTimeout from 'react-timeout';
import _ from 'lodash';
import classNames from 'classnames';
import { StickyContainer } from 'react-sticky';
import AltHeaderComponent from 'components/AltHeader';
import { Waypoint } from 'react-waypoint';
import Icon from 'components/Icon';
import Tile from 'components/TileLegacy';
import Dropdown from 'components/Dropdown';
import { Link } from 'react-router';
import { scroller, Element } from 'react-scroll';
import { noop } from 'helpers/GlobalHelper';
import { getTheme, webThemeIds } from 'helpers/ThemeHelper';
import { LIVE_EPG_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import {
  getTealiumLiveEvents,
  handleTealiumButtonAction,
  setSegmentRestartedPlaybackGlobal,
  TrackEvents,
} from '@foxcorp/delta-web-analytics';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { OVERLAY_PRIMETIME_DATES, OVERLAY_LIVE_CTA_OVERFLOW } from 'helpers/FilterHelper';
import { requiresAuthElement } from 'helpers/VideoProcessorHelper';
import {
  isSameDay,
  addDays,
  getHours,
  setHours,
  stringToDate,
  now,
  format,
  FORMAT_YEAR_MONTH_DAY,
  FORMAT_WEEKDAY_MONTH_DAY,
  parse,
} from 'helpers/DateTimeHelper';
import { isUrlPath, helperGetFNCLiveUrl } from 'helpers/RouteHelper';
import {
  isInEPGDateRange,
  getDayLabel,
  getTimezone,
  getVideoData,
  getVideoTimeData,
  getDateDropdownFormat,
  getDateDropdownIcon,
  getTodaysDate,
} from 'screens/Live/LiveHelpers';
import PrimeTimeItemComponent from 'screens/Live/components/PrimeTimeItem';
import listStyle from './MobileList.module.scss';
import altHeaderTheme from './AltHeaderPrimeTime.css';
import Style from './PrimeTimeGrid.module.scss';

const dropdownSharedTheme = getTheme(webThemeIds.Live.Dropdown);
const dropdownPrimetimeTheme = getTheme(webThemeIds.Live.DropdownPrimetime);
const dropdownTheme = { ...dropdownSharedTheme, ...dropdownPrimetimeTheme };
const PRIME_TIME_GRID_SCROLL_PREFIX = 'PRIME_TIME_GRID_SCROLL_PREFIX';
const TILE_IMAGE_LEFT = 'tile-image-left';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    renderStickyHeader: false,
    renderDesktopGrid: true,
    showScrollingDate: false,
    showDesktopTheme: true,
    defaultDateFormat: true,
    showMobileControls: false,
  },
  [SITE_PLATFORM_WEB]: {
    renderStickyHeader: false,
    renderDesktopGrid: true,
    showScrollingDate: false,
    showDesktopTheme: true,
    defaultDateFormat: true,
    showMobileControls: false,
  },
  [SITE_PLATFORM_TABLET]: {
    renderStickyHeader: true,
    renderDesktopGrid: false,
    showScrollingDate: true,
    showDesktopTheme: false,
    defaultDateFormat: false,
    showMobileControls: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    renderStickyHeader: true,
    renderDesktopGrid: false,
    showScrollingDate: true,
    showDesktopTheme: false,
    defaultDateFormat: false,
    showMobileControls: true,
  },
};

class PrimeTimeGrid extends Component {
  constructor(props) {
    super(props);

    this.state = {
      seeAllClicked: false,
      selectedChannelIdx: 0,
      dropdownOpen: false,
      openIdx: null,
      openIdxDate: null,
      dateLabel: [],
    };

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.watchLiveClick = this.watchLiveClick.bind(this);
    this.restartClick = this.restartClick.bind(this);
    this.toggleItem = this.toggleItem.bind(this);
  }

  componentDidMount() {
    const { scrollToDateOnMount, selectedDate } = this.props;
    const date = selectedDate;
    if (date && scrollToDateOnMount) {
      this.scrollToDate(date, null, -175);
    }
  }

  getDropdownItems() {
    const { setSelectedDate, selectedDate } = this.props;
    const { dropdownOpen: isOpen } = this.state;
    const dates = this.uniqueDatesFromPanels(FORMAT_YEAR_MONTH_DAY);
    const dateFormat = getDateDropdownFormat(isOpen);

    const items = dates.map((dateString, idx) => {
      const selectedHour = getHours(selectedDate);
      const date = setHours(stringToDate(dateString), selectedHour);

      const onClickParams = date;
      const isFirstItem = idx === 0;
      const isDefaultSelected = !selectedDate && isFirstItem;
      const isCurrentlySelected = isSameDay(selectedDate, date);
      const isSelected = isDefaultSelected || isCurrentlySelected || false;

      return {
        date,
        name: format(date, dateFormat),
        onClickParams,
        onClick: date => {
          setSelectedDate(date);
          this.scrollToDate(date);
        },
        isSelected,
      };
    });
    return items;
  }

  getSelectedDate() {
    const { selectedDate } = this.props;
    return selectedDate === null ? now() : stringToDate(selectedDate);
  }

  handleWaypointScroll(dayLabel, dateLabel, { currentPosition, previousPosition }) {
    const { state } = this;
    if (currentPosition === Waypoint.above && previousPosition === Waypoint.inside) {
      this.setState({
        dateLabel: [...state.dateLabel, dateLabel],
      });
    } else if (currentPosition === Waypoint.inside && previousPosition === Waypoint.above) {
      const newDates = _.dropRight(state.dateLabel);
      this.setState({
        dateLabel: newDates,
      });
    }
  }

  restartClick(video) {
    const { title, subtitleName, callSign } = getVideoData(video);

    const { isCurrent } = getVideoTimeData({ video });

    const { account, resetStillWatchingTimeout } = this.props;

    const { global_restartButton: labelRestart } = window.Language;

    const { [TrackEvents.SCHEDULE_RESTART]: tealiumScheduleRestart } = getTealiumLiveEvents();
    handleTealiumButtonAction({ text: labelRestart, title, subtitle: subtitleName, account });
    tealiumScheduleRestart({ network: callSign });
    if (isCurrent) setSegmentRestartedPlaybackGlobal(true);
    if (resetStillWatchingTimeout) resetStillWatchingTimeout();
  }

  scrollToDate(date, scrollDelay, offset) {
    if (!date) return;
    const { setTimeout } = this.props;
    const isFNCPage = isUrlPath(helperGetFNCLiveUrl());
    if (isFNCPage) return;

    const delay = scrollDelay || 300;

    const dateString = format(date, FORMAT_YEAR_MONTH_DAY);
    let scrollOffset = offset;

    if (!offset) {
      const sameDay = isSameDay(date, now());
      scrollOffset = sameDay ? -190 : -100;
    }

    const scrollOptions = {
      smooth: true,
      offset: scrollOffset,
    };

    const doScroll = () => {
      const elementName = `${PRIME_TIME_GRID_SCROLL_PREFIX}${dateString}`;
      const elementExists = document && document.getElementsByName(elementName).length;
      if (elementExists) scroller.scrollTo(elementName, scrollOptions);
      return null;
    };

    setTimeout(doScroll, delay);
  }

  toggleDropdown() {
    const { resetStillWatchingTimeout, setOverlay } = this.props;
    const { dropdownOpen } = this.state;
    this.setState({ dropdownOpen: !dropdownOpen });
    setOverlay(OVERLAY_PRIMETIME_DATES);
    resetStillWatchingTimeout();
  }

  toggleItem(rowIdx, date) {
    const { openIdx, openIdxDate } = this.state;
    let newOpenIdx = rowIdx;
    let newOpenDate = date;
    if (rowIdx === openIdx && date === openIdxDate) {
      newOpenIdx = null;
      newOpenDate = null;
    }
    this.setState({ openIdx: newOpenIdx, openIdxDate: newOpenDate });
  }

  uniqueDatesFromPanels(selectedFormat) {
    const { primeTimePanels } = this.props;
    const items = _.get(primeTimePanels, 'member.0.items.member', []);

    // get valid dates;
    const dates = items.map(item => {
      if (!isInEPGDateRange(item.startDate)) return null;
      return format(stringToDate(item.startDate), selectedFormat);
    });

    // remove null entries and leave only with unique dates.
    const uniqueDates = _.remove(_.uniq(dates), date => date);
    return uniqueDates;
  }

  watchLiveClick(video, channelIdx) {
    const { title, subtitleName, callSign } = getVideoData(video);

    const { onChannelSelect, account, resetStillWatchingTimeout } = this.props;

    const { global_watchLiveButton: labelWatchLive } = window.Language;

    const { [TrackEvents.SCHEDULE_WATCH_LIVE]: tealiumScheduleWatchLive } = getTealiumLiveEvents();
    tealiumScheduleWatchLive({ network: callSign });
    onChannelSelect({ selectedChannel: channelIdx });
    handleTealiumButtonAction({ text: labelWatchLive, title, subtitle: subtitleName, account });
    if (resetStillWatchingTimeout) resetStillWatchingTimeout();
  }

  renderCTAButtons({ video, channelIdx }) {
    if (!video) return null;

    const { requiresAuthLive, shouldRenderLiveCta, shouldRenderVODCta, watchUrl } = getVideoData(
      video,
    );

    const { isCurrent, isPast } = getVideoTimeData({ video });
    const { isAuthenticated } = this.props;

    const {
      global_watchLiveButton: labelWatchLive,
      global_restartButton: labelRestart,
    } = window.Language;

    const canRestart = shouldRenderVODCta && (isCurrent || isPast);
    const lockIcon = requiresAuthElement({
      requiresAuth: requiresAuthLive,
      lockClass: Style.lock,
      isAuthenticated,
      viewBox: '0 4 24 20',
    });

    const restartButton = canRestart ? (
      <div className={Style.restart}>
        <Link
          to={watchUrl}
          onClick={() => {
            this.restartClick(video);
          }}
        >
          <Icon color="white" icon="restart" />
          <span>
            {lockIcon} {labelRestart}
          </span>
        </Link>
      </div>
    ) : null;

    const watchLiveButton = shouldRenderLiveCta ? (
      <div className={Style.watchLive}>
        <Link
          onClick={() => {
            this.watchLiveClick(video, channelIdx);
          }}
        >
          <Icon color="white" icon="play-button" />
          <span>
            {lockIcon} {labelWatchLive}
          </span>
        </Link>
      </div>
    ) : null;

    return (
      <div>
        {restartButton}
        {watchLiveButton}
      </div>
    );
  }

  renderDateLabel() {
    const { dateLabel } = this.state;
    const { breakpoint } = this.props;
    const { showScrollingDate } = BREAKPOINTS[breakpoint];
    const { epg_todayHeader: todayHeader, epg_tomorrowHeader: tomorrowHeader } = window.Language;
    let header = null;

    let selectedDate = now();
    const defaultDate = stringToDate(selectedDate);

    if (showScrollingDate) {
      const hasDateLabel = dateLabel && dateLabel.length > 1;
      const lastDateLabel = hasDateLabel ? dateLabel[dateLabel.length - 1] : null;

      const parsedDate = hasDateLabel ? parse(lastDateLabel, FORMAT_WEEKDAY_MONTH_DAY) : null;
      selectedDate = parsedDate || defaultDate;
    } else {
      selectedDate = this.getSelectedDate();
    }

    const today = now();
    const tomorrow = addDays(now(), 1);
    const isToday = isSameDay(selectedDate, today);
    const isTomorrow = isSameDay(selectedDate, tomorrow);
    if (isToday) header = todayHeader;
    if (isTomorrow) header = tomorrowHeader;
    if (!header) return null;
    return <div className={Style.tabDateLabel}>{header}</div>;
  }

  renderDesktop() {
    return (
      <div className={Style.container}>
        <div className={Style.headerRow}>{this.renderDropdown()}</div>
        {this.renderPrimeTimePanels()}
      </div>
    );
  }

  renderDesktopItems(panels) {
    const { initialDisplayCount, onTitleClick, processor, breakpoint } = this.props;
    const { seeAllClicked, selectedChannelIdx } = this.state;

    const grid = panels.map((video, itemIndex) => {
      if (initialDisplayCount && !seeAllClicked && itemIndex >= initialDisplayCount) return null;
      const { id } = video;
      const { startDate, isLiveNow } = getVideoData(video);
      const d = stringToDate(startDate);
      const startTime = `${format(d, 'h')}:${format(d, 'mm')}${format(d, 'a')} ${getTimezone()}`;
      const props = processor({ item: video });
      const tileClassName = isLiveNow ? classNames(Style.tile, Style.isLiveNow) : Style.tile;

      props.onTileClick = () => {
        this.watchLiveClick(video, selectedChannelIdx);
      };
      props.onTitleClick = onTitleClick;
      props.itemIndex = itemIndex;
      props.tileType = TILE_IMAGE_LEFT;
      props.renderImage = image =>
        renderImage({
          breakpoint,
          imageType: LIVE_EPG_THUMBNAIL_IMAGE,
          ...image,
        });
      props.theme = Style;

      return (
        <div className={Style.row} key={id}>
          <div className={tileClassName}>
            <Tile {...props} />
          </div>
          <div className={Style.cta}>
            {this.renderCTAButtons({ video, channelIdx: selectedChannelIdx })}
          </div>
          <div className={Style.time}>
            <span>{startTime}</span>
          </div>
        </div>
      );
    });

    return grid;
  }

  renderDropdown() {
    const { selectedDate, breakpoint } = this.props;
    const { dropdownOpen: isOpen, dateLabel } = this.state;
    const { defaultDateFormat, showMobileControls } = BREAKPOINTS[breakpoint];
    const selectedFormat = getDateDropdownFormat(defaultDateFormat ? isOpen : true);
    const dates = this.uniqueDatesFromPanels(FORMAT_YEAR_MONTH_DAY);

    if (!dates || dates.length === 0) return null;

    const items = this.getDropdownItems();
    const date = _.get(items, '0.date');

    const useSelectedDate = defaultDateFormat && selectedDate;
    const selectedDateText = format(stringToDate(selectedDate), selectedFormat);
    const dateText = format(stringToDate(date), selectedFormat);

    const defaultText = useSelectedDate ? selectedDateText : dateText;
    const text = dateLabel.length > 1 ? dateLabel[dateLabel.length - 1] : defaultText;

    return (
      <div className={Style.dropdown}>
        <Dropdown
          theme={dropdownTheme}
          items={items}
          getIcon={getDateDropdownIcon(isOpen)}
          defaultText={text}
          onOpenDropdown={this.toggleDropdown}
          onCloseDropdown={this.toggleDropdown}
          displayResults={isOpen}
          showMobileCancel={showMobileControls}
          expanded={isOpen}
          className={classNames(Style.dropdown, dropdownTheme.dateDropdown)}
        />
      </div>
    );
  }

  renderGrid(panels, date) {
    const { filterGridByDay, selectedDate } = this.props;
    const selectedDateFormatted = format(selectedDate, FORMAT_YEAR_MONTH_DAY);
    const shouldHideDay = filterGridByDay && date !== selectedDateFormatted;
    if (!isInEPGDateRange(date) || shouldHideDay) return null;

    const TODAY = getTodaysDate();
    const { breakpoint, initialDisplayCount, overlay } = this.props;
    const { seeAllClicked } = this.state;
    const { renderDesktopGrid, showDesktopTheme } = BREAKPOINTS[breakpoint];
    const currentDate = stringToDate(date);
    const dayLabel = renderDesktopGrid ? getDayLabel(currentDate) : null;
    const dateKey = format(currentDate, FORMAT_YEAR_MONTH_DAY);
    const dateLabel = format(currentDate, FORMAT_WEEKDAY_MONTH_DAY);
    const currentTheme = showDesktopTheme ? Style : listStyle;
    const items = renderDesktopGrid
      ? this.renderDesktopItems(panels)
      : this.renderMobileItems(panels, date);
    const overflowDropdownOpen =
      overlay === OVERLAY_LIVE_CTA_OVERFLOW ? Style.childDropdownOpen : Style.childDropdownClosed;

    const dayElement = dayLabel ? <span className={currentTheme.today}>{dayLabel}</span> : null;
    const dateElement = <div className={currentTheme.currentDate}>{dateLabel}</div>;

    const { paginationMoreButton: seeAllText } = window.Language;
    const renderSeeAll = initialDisplayCount && !seeAllClicked;
    const onClickSeeAll = () => this.setState({ seeAllClicked: true });

    return (
      <div
        className={classNames(currentTheme.gridWrapper, overflowDropdownOpen)}
        key={`primetime-grid-${dateKey}`}
      >
        <Element name={`${PRIME_TIME_GRID_SCROLL_PREFIX}${dateKey}`}>
          {renderDesktopGrid ? (
            <div className={currentTheme.dateHeader}>
              {dayElement}
              {dateElement}
            </div>
          ) : (
            <Waypoint
              topOffset="200px"
              onEnter={this.handleWaypointScroll.bind(this, dayLabel, dateLabel)}
              onLeave={this.handleWaypointScroll.bind(this, dayLabel, dateLabel)}
            >
              {TODAY !== dateLabel && (
                <div>
                  {dayElement}
                  {dateElement}
                </div>
              )}
            </Waypoint>
          )}
          {items}
          {renderSeeAll && (
            <div className={Style.seeAllButtonContainer}>
              <button className={Style.seeAllButton} onClick={onClickSeeAll}>
                {seeAllText}
              </button>
            </div>
          )}
        </Element>
      </div>
    );
  }

  renderMobile() {
    return this.renderPrimeTimePanels();
  }

  renderMobileItems(panels, date) {
    const {
      initialDisplayCount,
      isAuthenticated,
      breakpoint,
      overlay,
      setOverlay,
      favoritesManager,
    } = this.props;
    const { showMobileControls } = BREAKPOINTS[breakpoint];
    if (!showMobileControls) return null;

    const { openIdx, openIdxDate, seeAllClicked } = this.state;
    const primeTimeItemProps = {
      isAuthenticated,
      favoritesManager,
      date,
      breakpoint,
      overlay,
      setOverlay,
      watchLiveClick: this.watchLiveClick,
      restartClick: this.restartClick,
    };
    return panels.map((item, idx) => {
      if (initialDisplayCount && !seeAllClicked && idx >= initialDisplayCount) return null;
      return (
        <PrimeTimeItemComponent
          {...primeTimeItemProps}
          item={item}
          idx={idx}
          openIdx={openIdx}
          openIdxDate={openIdxDate}
          toggleItem={this.toggleItem}
        />
      );
    });
  }

  renderPrimeTimePanels() {
    const { primeTimePanels, breakpoint, overlay, isNavScheduleRendered } = this.props;
    const { renderStickyHeader } = BREAKPOINTS[breakpoint];

    const panels = _.get(primeTimePanels, 'member.0.items.member');

    const groupedBySchedules = _.groupBy(panels, panel =>
      format(stringToDate(panel.startDate), FORMAT_YEAR_MONTH_DAY),
    );
    const sortedSchedules = _.map(groupedBySchedules, (schedule, key) =>
      this.renderGrid(schedule, key),
    );

    const overflowDropdownOpen =
      overlay === OVERLAY_LIVE_CTA_OVERFLOW ? Style.childDropdownOpen : Style.childDropdownClosed;

    return (
      <StickyContainer>
        {
          <AltHeaderComponent
            theme={altHeaderTheme}
            topOffset={-90}
            isAltHeader
            isNavScheduleRendered={isNavScheduleRendered}
            className={Style.altHeaderWrapper}
            renderAltHeader={renderStickyHeader}
          >
            {this.renderDateLabel()}
            {this.renderDropdown()}
          </AltHeaderComponent>
        }
        <div className={overflowDropdownOpen}>{sortedSchedules}</div>
      </StickyContainer>
    );
  }

  render() {
    const { primeTimePanels, breakpoint } = this.props;
    const { renderDesktopGrid } = BREAKPOINTS[breakpoint];

    if (!primeTimePanels) return null;

    return renderDesktopGrid ? this.renderDesktop() : this.renderMobile();
  }
}

PrimeTimeGrid.defaultProps = {
  account: null,
  coordinates: null,
  favoritesManager: null,
  filterGridByDay: false,
  initialDisplayCount: null,
  isAuthenticated: null,
  isNavScheduleRendered: null,
  onTitleClick: noop,
  overlay: null,
  primeTimePanels: {},
  processor: noop,
  resetStillWatchingTimeout: noop,
  scrollToDateOnMount: true,
  selectedDate: null,
  setTimeout: noop,
};

PrimeTimeGrid.propTypes = {
  account: PropTypes.shape({}),
  breakpoint: PropTypes.string.isRequired,
  coordinates: PropTypes.shape({}),
  favoritesManager: PropTypes.shape({
    favorites: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    setFavorites: PropTypes.func.isRequired,
    deleteFavorites: PropTypes.func.isRequired,
  }),
  filterGridByDay: PropTypes.bool,
  initialDisplayCount: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  isNavScheduleRendered: PropTypes.bool,
  onChannelSelect: PropTypes.func.isRequired,
  onTitleClick: PropTypes.func,
  overlay: PropTypes.string,
  primeTimePanels: PropTypes.shape({}),
  processor: PropTypes.func,
  resetStillWatchingTimeout: PropTypes.func,
  scrollToDateOnMount: PropTypes.bool,
  selectedDate: PropTypes.instanceOf(Date),
  setOverlay: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  setTimeout: PropTypes.func,
};

export default ReactTimeout(PrimeTimeGrid);
